import React, { useState } from 'react';
import InvitationDesignerV2 from '../_components/InvitationDesigner/InvitationDesignerV2';
import DesignTestPanel from '../_components/DesignTestPanel/DesignTestPanel';
import { Event, InvitationType } from '../_graphql/generated/operations';
import { InvitationDesign } from '../_types/invitation-design-v2';

// Mock test data
const mockEvent: Event = {
  id: 'test-event-1',
  eventName: 'Sarah & Michael\'s Wedding',
  eventDateTime: new Date('2025-06-15T16:00:00').getTime(),
  createdAt: Date.now(),
  updatedAt: Date.now(),
  invitations: [],
  invitationTypes: []
};

// Commented out hardcoded invitation types - now using real GraphQL API
// const mockInvitationTypes: InvitationType[] = [
//   {
//     id: 'type-1',
//     eventId: 'test-event-1',
//     name: 'Save the Date',
//     description: 'Early notification for guests to save the wedding date',
//     designCustomizations: null,
//     createdAt: Date.now(),
//     updatedAt: Date.now(),
//     event: mockEvent
//   },
//   {
//     id: 'type-2',
//     eventId: 'test-event-1',
//     name: 'Full Invitation',
//     description: 'Complete invitation with all event details',
//     designCustomizations: null,
//     createdAt: Date.now(),
//     updatedAt: Date.now(),
//     event: mockEvent
//   },
//   {
//     id: 'type-3',
//     eventId: 'test-event-1',
//     name: 'Ceremony Only',
//     description: 'Invitation for ceremony attendance only',
//     designCustomizations: null,
//     createdAt: Date.now(),
//     updatedAt: Date.now(),
//     event: mockEvent
//   }
// ];

// Start with empty invitation types - will be populated via create button
const mockInvitationTypes: InvitationType[] = [];

// Add invitation types to the event
mockEvent.invitationTypes = mockInvitationTypes;

const InvitationDesignerStandalone: React.FC = () => {
  const [savedDesigns, setSavedDesigns] = useState<Map<string, InvitationDesign>>(new Map());
  const [currentDesign, setCurrentDesign] = useState<InvitationDesign | null>(null);
  const [currentInvitationType, setCurrentInvitationType] = useState<InvitationType | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(true);

  const handleSave = (design: InvitationDesign, invitationType?: InvitationType) => {
    if (invitationType) {
      // Store the V2 design
      const newDesigns = new Map(savedDesigns);
      newDesigns.set(invitationType.id, design);
      setSavedDesigns(newDesigns);
      
      // Store current invitation type and design
      setCurrentInvitationType(invitationType);
      setCurrentDesign(design);
      
      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleCancel = () => {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-lg font-semibold">Invitation Designer Test</h1>
            <a 
              href="/" 
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-20 right-4 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg z-50 animate-fade-in">
          Design saved successfully!
        </div>
      )}

      {/* Designer component */}
      <div className="h-[calc(100vh-4rem)]">
        <InvitationDesignerV2
          event={mockEvent}
          onSave={handleSave}
          onCancel={handleCancel}
          // Pass the latest design for the current type
          initialDesign={currentInvitationType ? savedDesigns.get(currentInvitationType.id) : undefined}
          selectedInvitationType={currentInvitationType || undefined}
        />
      </div>

      {/* Debug info */}
      <div className="fixed bottom-4 left-4 bg-black/80 text-white text-xs p-3 rounded-md max-w-sm">
        <div className="font-semibold mb-1">Test Mode</div>
        <div>Event: {mockEvent.eventName}</div>
        <div>Types: {mockInvitationTypes.length}</div>
        <div>Saved Designs: {savedDesigns.size}</div>
        <button
          onClick={() => setShowTestPanel(!showTestPanel)}
          className="mt-2 text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
        >
          {showTestPanel ? 'Hide' : 'Show'} Test Panel
        </button>
      </div>

      {/* Design Test Panel */}
      <DesignTestPanel
        designV1={undefined}
        designV2={currentDesign}
        isOpen={showTestPanel}
        onClose={() => setShowTestPanel(false)}
        onApplyDesign={(updatedDesign) => {
          setCurrentDesign(updatedDesign);
          
          // Update the saved designs
          if (currentInvitationType) {
            const newDesigns = new Map(savedDesigns);
            newDesigns.set(currentInvitationType.id, updatedDesign);
            setSavedDesigns(newDesigns);
          }
          
          // Show success message
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        }}
      />
    </div>
  );
};

export default InvitationDesignerStandalone;